<template>
  <section class="catalog-products">
    <header class="catalog-products__header">
      <div>
        <span class="catalog-products__eyebrow">PRODUTOS DA VITRINE</span>
        <h2>Escolha o que aparece no catálogo</h2>
        <p>
          As alterações feitas aqui usam o mesmo cadastro de produtos do ERP.
          Você pode publicar, ajustar o preço, trocar a foto ou abrir o cadastro
          completo.
        </p>
        <p>Fotos JPG, PNG ou WebP até 5 MB. Cada foto será ajustada em 1:1, com recorte mínimo de 800 × 800 px.</p>
      </div>
      <span
        class="catalog-products__draft-count"
        aria-live="polite"
        aria-atomic="true"
      >
        <strong>{{ dirtyCount }}</strong>
        {{ dirtyCount === 1 ? "alteração pendente" : "alterações pendentes" }}
      </span>
    </header>

    <div class="catalog-products__toolbar">
      <form class="catalog-products__search" @submit.prevent="applySearch">
        <i class="la la-search" aria-hidden="true"></i>
        <input
          v-model.trim="searchInput"
          type="search"
          maxlength="100"
          :disabled="saving || imageValidationBusy"
          placeholder="Buscar por nome, código ou referência"
          aria-label="Buscar produtos"
          @input="scheduleSearch"
        />
        <button
          v-if="searchInput"
          type="button"
          :disabled="saving || imageValidationBusy"
          title="Limpar busca"
          aria-label="Limpar busca"
          @click="clearSearch"
        >
          <i class="la la-times" aria-hidden="true"></i>
        </button>
      </form>

      <button
        type="button"
        class="catalog-products__refresh"
        :disabled="loading || saving || imageValidationBusy || hasDrafts"
        :title="
          hasDrafts
            ? 'Publique ou desfaça as alterações antes de atualizar.'
            : 'Atualizar produtos'
        "
        @click="loadProducts"
      >
        <i class="la la-sync" aria-hidden="true"></i>
        Atualizar
      </button>
    </div>

    <article class="catalog-products__panel">
      <div v-if="loading" class="catalog-products__loading">
        <span class="catalog-products__spinner"></span>
        Carregando produtos...
      </div>

      <div v-else-if="!products.length" class="catalog-products__empty">
        <span><i class="la la-box-open" aria-hidden="true"></i></span>
        <h3>Nenhum produto encontrado</h3>
        <p v-if="appliedSearch">
          Tente outro termo ou limpe a busca para ver todos os produtos.
        </p>
        <p v-else>Cadastre um produto ativo no ERP para começar sua vitrine.</p>
      </div>

      <div v-else class="catalog-products__table-wrap">
        <table class="catalog-products__table">
          <thead>
            <tr>
              <th class="is-photo" scope="col">Fotos</th>
              <th scope="col">Produto</th>
              <th class="is-price" scope="col">Preço no ERP</th>
              <th class="is-publish" scope="col">No catálogo</th>
              <th class="is-action" scope="col">
                <span class="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="product in products" :key="product.id">
              <td class="is-photo" data-label="Foto">
                <div
                  class="catalog-product-gallery"
                  role="group"
                  :aria-label="`Fotos de ${product.descricao}`"
                >
                  <div class="catalog-product-photo is-principal">
                    <span class="catalog-product-photo__label">Principal</span>
                    <div class="catalog-product-photo__preview">
                      <img
                        v-if="productImage(product)"
                        :src="productImage(product)"
                        :alt="`Foto principal de ${product.descricao}`"
                      />
                      <i v-else class="la la-image" aria-hidden="true"></i>
                    </div>
                    <div class="catalog-product-photo__actions">
                      <input
                        :id="`catalog-product-image-${product.id}`"
                        class="sr-only"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        :disabled="saving || imageValidationBusy"
                        :aria-label="`Selecionar foto principal de ${product.descricao}`"
                        @change="selectProductImage(product, $event)"
                      />
                      <label
                        :for="`catalog-product-image-${product.id}`"
                        :class="{
                          'is-disabled': saving || imageValidationBusy,
                        }"
                        :aria-label="
                          (productImage(product) ? 'Trocar' : 'Adicionar') +
                          ' foto principal de ' +
                          product.descricao
                        "
                      >
                        {{ productImage(product) ? "Trocar" : "Adicionar" }}
                      </label>
                      <button
                        v-if="productImage(product)"
                        type="button"
                        :disabled="saving || imageValidationBusy"
                        :aria-label="`Remover foto principal de ${product.descricao}`"
                        @click="removeProductImage(product)"
                      >
                        Remover
                      </button>
                      <button
                        v-if="hasImageDraft(product)"
                        type="button"
                        class="is-undo"
                        :disabled="saving || imageValidationBusy"
                        :aria-label="`Desfazer alteração da foto principal de ${product.descricao}`"
                        @click="undoProductImage(product)"
                      >
                        Desfazer
                      </button>
                      <input
                        :id="`catalog-product-complementary-next-${product.id}`"
                        class="sr-only"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        :disabled="
                          saving ||
                          imageValidationBusy ||
                          !canAddComplementaryImage(product)
                        "
                        :aria-label="`Adicionar próxima foto complementar de ${product.descricao}`"
                        @change="selectNextComplementaryImage(product, $event)"
                      />
                      <label
                        class="is-add-complementary"
                        :class="{
                          'is-disabled':
                            saving ||
                            imageValidationBusy ||
                            !canAddComplementaryImage(product),
                        }"
                        :for="`catalog-product-complementary-next-${product.id}`"
                        :aria-disabled="
                          saving ||
                          imageValidationBusy ||
                          !canAddComplementaryImage(product)
                        "
                        :aria-label="`Adicionar próxima foto complementar de ${product.descricao}`"
                        :title="
                          canAddComplementaryImage(product)
                            ? 'Adicionar foto exclusiva do catálogo'
                            : 'Este produto já possui o máximo de três fotos'
                        "
                      >
                        <i class="la la-plus" aria-hidden="true"></i>
                        Adicionar
                      </label>
                    </div>
                  </div>

                  <div
                    v-for="slot in complementarySlots"
                    :key="slot"
                    class="catalog-product-photo is-complementary"
                  >
                    <span class="catalog-product-photo__label">
                      {{ slot + 1 }}
                    </span>
                    <div class="catalog-product-photo__preview">
                      <img
                        v-if="complementaryImage(product, slot)"
                        :src="complementaryImage(product, slot)"
                        :alt="`Foto ${slot + 1} de ${product.descricao}`"
                      />
                      <i v-else class="la la-image" aria-hidden="true"></i>
                    </div>
                    <div class="catalog-product-photo__actions">
                      <input
                        :id="`catalog-product-complementary-${product.id}-${slot}`"
                        class="sr-only"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        :disabled="saving || imageValidationBusy"
                        :aria-label="`Selecionar foto ${slot + 1} de ${
                          product.descricao
                        }`"
                        @change="
                          selectComplementaryImage(product, slot, $event)
                        "
                      />
                      <label
                        :for="`catalog-product-complementary-${product.id}-${slot}`"
                        :class="{
                          'is-disabled': saving || imageValidationBusy,
                        }"
                        :aria-label="
                          (complementaryImage(product, slot)
                            ? 'Trocar'
                            : 'Adicionar') +
                          ' foto ' +
                          (slot + 1) +
                          ' de ' +
                          product.descricao
                        "
                      >
                        {{
                          complementaryImage(product, slot)
                            ? "Trocar"
                            : "Adicionar"
                        }}
                      </label>
                      <button
                        v-if="complementaryImage(product, slot)"
                        type="button"
                        :disabled="saving || imageValidationBusy"
                        :aria-label="`Remover foto ${slot + 1} de ${
                          product.descricao
                        }`"
                        @click="removeComplementaryImage(product, slot)"
                      >
                        Remover
                      </button>
                      <button
                        v-if="hasComplementaryImageDraft(product, slot)"
                        type="button"
                        class="is-undo"
                        :disabled="saving || imageValidationBusy"
                        :aria-label="`Desfazer alteração da foto ${
                          slot + 1
                        } de ${product.descricao}`"
                        @click="undoComplementaryImage(product, slot)"
                      >
                        Desfazer
                      </button>
                    </div>
                  </div>
                </div>
              </td>

              <td data-label="Produto">
                <div class="catalog-product-name">
                  <strong>{{ product.descricao }}</strong>
                  <small>
                    <span v-if="product.codigo">Cód. {{ product.codigo }}</span>
                    <span v-if="product.grupo">{{ product.grupo }}</span>
                  </small>
                  <em v-if="!product.catalogo_elegivel">
                    {{
                      product.motivo_inelegivel ||
                      "Produto indisponível para publicação."
                    }}
                  </em>
                </div>
              </td>

              <td class="is-price" data-label="Preço">
                <label class="catalog-product-price">
                  <span>R$</span>
                  <input
                    type="text"
                    inputmode="decimal"
                    maxlength="14"
                    :value="productPriceInput(product)"
                    :disabled="saving"
                    :class="{ 'is-invalid': productPriceError(product) }"
                    :aria-invalid="Boolean(productPriceError(product))"
                    :aria-label="`Preço de ${product.descricao}`"
                    @input="updateProductPrice(product, $event.target.value)"
                    @blur="normalizeProductPrice(product)"
                  />
                  <small v-if="productPriceError(product)">
                    {{ productPriceError(product) }}
                  </small>
                </label>
              </td>

              <td class="is-publish" data-label="No catálogo">
                <label
                  class="catalog-product-toggle"
                  :class="{
                    'is-disabled':
                      saving ||
                      (!product.catalogo_elegivel &&
                        !productPublished(product)),
                  }"
                >
                  <input
                    type="checkbox"
                    :checked="productPublished(product)"
                    :disabled="
                      saving ||
                      (!product.catalogo_elegivel && !productPublished(product))
                    "
                    :aria-label="`Publicar ${product.descricao} no catálogo`"
                    @change="toggleProduct(product, $event.target.checked)"
                  />
                  <span><i></i></span>
                  <em>{{ productPublished(product) ? "Ativo" : "Inativo" }}</em>
                </label>
              </td>

              <td class="is-action" data-label="Ações">
                <button
                  type="button"
                  class="catalog-product-edit"
                  :disabled="saving || imageValidationBusy"
                  title="Editar no cadastro de produtos"
                  @click="editProduct(product)"
                >
                  <i class="la la-pen" aria-hidden="true"></i>
                  Editar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer v-if="meta.total" class="catalog-products__pagination">
        <p>
          Exibindo
          <strong>{{ meta.from || 1 }}–{{ meta.to || products.length }}</strong>
          de <strong>{{ meta.total }}</strong> produtos
        </p>
        <div>
          <button
            type="button"
            :disabled="
              loading || saving || imageValidationBusy || meta.current_page <= 1
            "
            aria-label="Página anterior"
            @click="goToPage(meta.current_page - 1)"
          >
            <i class="la la-angle-left" aria-hidden="true"></i>
          </button>
          <button
            v-for="pageNumber in visiblePages"
            :key="pageNumber"
            type="button"
            :class="{ active: pageNumber === meta.current_page }"
            :disabled="loading || saving || imageValidationBusy"
            :aria-current="pageNumber === meta.current_page ? 'page' : null"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </button>
          <button
            type="button"
            :disabled="
              loading ||
              saving ||
              imageValidationBusy ||
              meta.current_page >= meta.last_page
            "
            aria-label="Próxima página"
            @click="goToPage(meta.current_page + 1)"
          >
            <i class="la la-angle-right" aria-hidden="true"></i>
          </button>
        </div>
      </footer>
    </article>

    <catalog-image-crop-modal
      :file="cropSelection && cropSelection.file"
      @apply="applyCropSelection"
      @cancel="cropSelection = null"
    />

    <div class="catalog-products__publish-bar">
      <div>
        <span><i class="la la-cloud-upload-alt" aria-hidden="true"></i></span>
        <p>
          <strong>Pronto para atualizar a vitrine?</strong>
          <small>
            {{
              hasDrafts
                ? "As alterações só aparecem no catálogo depois da publicação."
                : "Publique para sincronizar e garantir que a vitrine está ativa."
            }}
          </small>
        </p>
      </div>
      <button
        type="button"
        :disabled="saving || imageValidationBusy"
        @click="saveAndPublish"
      >
        <span v-if="saving" class="catalog-products__spinner"></span>
        <i v-else class="la la-check" aria-hidden="true"></i>
        {{ saving ? "Publicando..." : "Salvar e publicar" }}
      </button>
    </div>
  </section>
</template>

<script>
import {
  CATALOG_IMAGE_LIMITS,
  buildCatalogProductPatch,
  catalogProductPriceChanged,
  extractCatalogCollection,
  normalizeCatalogPagination,
  normalizeCatalogProduct,
  normalizeDecimal,
  sanitizeCatalogPriceInput,
  shouldRemoveCatalogMainImage,
  validateCatalogImageDimensions,
  validateCatalogProductPrice,
} from "../catalogoDigitalConfig";
import {
  getCatalogManagementProducts,
  publishCatalogProducts,
} from "../services/catalogoDigitalApi";
import CatalogImageCropModal from "./CatalogImageCropModal.vue";

const DEFAULT_META = Object.freeze({
  current_page: 1,
  last_page: 1,
  per_page: 12,
  total: 0,
  from: null,
  to: null,
});
const MAX_PRODUCT_PRICE = 999999999.99;
const COMPLEMENTARY_SLOTS = Object.freeze([1, 2]);

export default {
  name: "CatalogoDigitalProductsTab",
  components: { CatalogImageCropModal },
  data() {
    return {
      products: [],
      drafts: {},
      meta: { ...DEFAULT_META },
      searchInput: "",
      appliedSearch: "",
      loading: false,
      saving: false,
      searchTimer: null,
      requestSequence: 0,
      allowRouteLeave: false,
      complementarySlots: COMPLEMENTARY_SLOTS,
      validatingImages: 0,
      componentDestroyed: false,
      cropSelection: null,
    };
  },
  computed: {
    dirtyCount() {
      return Object.keys(this.drafts).length;
    },
    hasDrafts() {
      return this.dirtyCount > 0;
    },
    imageValidationBusy() {
      return this.validatingImages > 0 || Boolean(this.cropSelection);
    },
    visiblePages() {
      const last = this.meta.last_page;
      const current = this.meta.current_page;
      const start = Math.max(1, Math.min(current - 2, last - 4));
      const end = Math.min(last, start + 4);
      const pages = [];
      for (let page = start; page <= end; page += 1) pages.push(page);
      return pages;
    },
  },
  created() {
    this.loadProducts();
    window.addEventListener("focus", this.handleWindowFocus);
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },
  beforeDestroy() {
    this.componentDestroyed = true;
    this.requestSequence += 1;
    window.removeEventListener("focus", this.handleWindowFocus);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    Object.keys(this.drafts).forEach((id) => this.revokeAllDraftPreviews(id));
  },
  methods: {
    async loadProducts(options = {}) {
      const requestId = ++this.requestSequence;
      if (!options.silent) this.loading = true;
      try {
        const data = await getCatalogManagementProducts({
          page: this.meta.current_page,
          per_page: this.meta.per_page,
          busca: this.appliedSearch,
          publicado: "todos",
        });
        if (requestId !== this.requestSequence) return;
        this.products = extractCatalogCollection(data, "produtos").map(
          normalizeCatalogProduct
        );
        this.meta = normalizeCatalogPagination(
          (data && data.meta) || data || {},
          this.meta
        );
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.notifyError(error, "Não foi possível carregar os produtos.");
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    handleWindowFocus() {
      if (!this.hasDrafts && !this.saving && !this.imageValidationBusy) {
        this.loadProducts({ silent: true });
      }
    },
    handleBeforeUnload(event) {
      if ((!this.hasDrafts && !this.imageValidationBusy) || this.saving) return;
      event.preventDefault();
      event.returnValue = "";
    },
    scheduleSearch() {
      if (this.saving || this.imageValidationBusy) return;
      if (this.searchTimer) window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(this.applySearch, 350);
    },
    applySearch() {
      if (this.searchTimer) window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
      if (this.saving || this.imageValidationBusy) return;
      const nextSearch = this.searchInput.trim();
      if (nextSearch === this.appliedSearch && this.meta.current_page === 1) {
        return;
      }
      this.appliedSearch = nextSearch;
      this.meta.current_page = 1;
      this.loadProducts();
    },
    clearSearch() {
      if (this.saving || this.imageValidationBusy) return;
      this.searchInput = "";
      this.applySearch();
    },
    goToPage(page) {
      if (this.saving || this.imageValidationBusy) return;
      const nextPage = Math.max(1, Math.min(Number(page), this.meta.last_page));
      if (nextPage === this.meta.current_page || this.loading) return;
      this.meta.current_page = nextPage;
      this.loadProducts();
    },
    draftFor(product) {
      return this.drafts[String(product.id)] || null;
    },
    complementaryProductImage(product, slot) {
      const images = Array.isArray(product.imagens_complementares)
        ? product.imagens_complementares
        : [];
      return (
        images.find((image) => Number(image.ordem) === Number(slot)) || null
      );
    },
    buildComplementaryDraft(product) {
      return COMPLEMENTARY_SLOTS.reduce((slots, slot) => {
        const original = this.complementaryProductImage(product, slot);
        slots[String(slot)] = {
          original_url: (original && original.imagem_url) || "",
          image_file: null,
          image_preview: "",
          image_crop: null,
          removed: false,
        };
        return slots;
      }, {});
    },
    ensureDraft(product) {
      const key = String(product.id);
      if (!this.drafts[key]) {
        this.$set(this.drafts, key, {
          id: Number(product.id),
          catalogo_digital: Boolean(product.catalogo_digital),
          original_catalogo_digital: Boolean(product.catalogo_digital),
          original_preco: normalizeDecimal(product.valor_venda) || "0.00",
          preco_input: this.formatPriceInput(product.valor_venda),
          preco_erro: "",
          original_image: product.imagem || "",
          image_file: null,
          image_preview: "",
          image_crop: null,
          remover_imagem: false,
          complementary_images: this.buildComplementaryDraft(product),
          remover_imagens_complementares: [],
        });
      }
      return this.drafts[key];
    },
    productPublished(product) {
      const draft = this.draftFor(product);
      return draft
        ? Boolean(draft.catalogo_digital)
        : Boolean(product.catalogo_digital);
    },
    productImage(product) {
      const draft = this.draftFor(product);
      if (!draft) return product.imagem || "";
      if (draft.image_preview) return draft.image_preview;
      if (draft.remover_imagem) return "";
      return product.imagem || draft.original_image || "";
    },
    hasImageDraft(product) {
      const draft = this.draftFor(product);
      return Boolean(
        draft &&
          (draft.image_file || draft.image_preview || draft.remover_imagem)
      );
    },
    complementaryImageState(product, slot) {
      const draft = this.draftFor(product);
      return (
        (draft &&
          draft.complementary_images &&
          draft.complementary_images[String(slot)]) ||
        null
      );
    },
    complementaryImage(product, slot) {
      const state = this.complementaryImageState(product, slot);
      if (state) {
        if (state.image_preview) return state.image_preview;
        if (state.removed) return "";
        return state.original_url || "";
      }

      const original = this.complementaryProductImage(product, slot);
      return (original && original.imagem_url) || "";
    },
    hasComplementaryImageDraft(product, slot) {
      const state = this.complementaryImageState(product, slot);
      return Boolean(state && (state.image_file || state.removed));
    },
    nextComplementarySlot(product) {
      return (
        COMPLEMENTARY_SLOTS.find(
          (slot) => !this.complementaryImage(product, slot)
        ) || null
      );
    },
    canAddComplementaryImage(product) {
      return Boolean(this.nextComplementarySlot(product));
    },
    formatPriceInput(value) {
      const normalized = normalizeDecimal(value);
      if (!normalized) return "";
      return Number(normalized).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    productPriceInput(product) {
      const draft = this.draftFor(product);
      return draft
        ? draft.preco_input
        : this.formatPriceInput(product.valor_venda);
    },
    productPriceError(product) {
      const draft = this.draftFor(product);
      return (draft && draft.preco_erro) || "";
    },
    validateProductPrice(draft) {
      const { normalized, error } = validateCatalogProductPrice(
        draft.preco_input,
        MAX_PRODUCT_PRICE
      );
      this.$set(draft, "preco_erro", error);
      return normalized;
    },
    draftPriceChanged(draft) {
      return catalogProductPriceChanged(draft);
    },
    updateProductPrice(product, value) {
      if (this.saving) return;
      const draft = this.ensureDraft(product);
      draft.preco_input = sanitizeCatalogPriceInput(value);
      this.validateProductPrice(draft);
      this.cleanDraftIfUnchanged(product);
    },
    normalizeProductPrice(product) {
      if (this.saving) return;
      const draft = this.draftFor(product);
      if (!draft) return;
      const normalized = this.validateProductPrice(draft);
      if (normalized) draft.preco_input = this.formatPriceInput(normalized);
      this.cleanDraftIfUnchanged(product);
    },
    toggleProduct(product, value) {
      if (this.saving) return;
      const draft = this.ensureDraft(product);
      draft.catalogo_digital = Boolean(value);
      this.cleanDraftIfUnchanged(product);
    },
    async readCatalogImageDimensions(file) {
      if (typeof window.createImageBitmap === "function") {
        let bitmap = null;
        try {
          bitmap = await window.createImageBitmap(file);
          return { width: bitmap.width, height: bitmap.height };
        } finally {
          if (bitmap && typeof bitmap.close === "function") bitmap.close();
        }
      }

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("image-read-failed"));
        reader.onload = () => {
          const image = new Image();
          image.onerror = () => reject(new Error("image-decode-failed"));
          image.onload = () =>
            resolve({ width: image.naturalWidth, height: image.naturalHeight });
          image.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
    },
    async validateCatalogImage(file) {
      if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
        this.$toast.error("Escolha uma imagem PNG, JPG ou WebP.");
        return false;
      }
      if (file.size > CATALOG_IMAGE_LIMITS.max_bytes) {
        this.$toast.error("A imagem deve ter no máximo 5 MB.");
        return false;
      }

      this.validatingImages += 1;
      try {
        const dimensions = await this.readCatalogImageDimensions(file);
        const result = validateCatalogImageDimensions(
          dimensions.width,
          dimensions.height
        );
        if (!result.valid && !this.componentDestroyed) {
          this.$toast.error(result.error);
        }
        return result.valid;
      } catch (error) {
        if (!this.componentDestroyed) {
          this.$toast.error(
            "Não foi possível ler a imagem. Escolha outro arquivo PNG, JPG ou WebP."
          );
        }
        return false;
      } finally {
        this.validatingImages = Math.max(0, this.validatingImages - 1);
      }
    },
    async selectProductImage(product, event) {
      if (this.saving || this.imageValidationBusy) return;
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      if (!(await this.validateCatalogImage(file))) return;
      if (this.componentDestroyed || this.saving) return;

      this.cropSelection = { product, slot: 0, file };
    },
    async selectNextComplementaryImage(product, event) {
      if (this.saving || this.imageValidationBusy) return;
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;

      if (!(await this.validateCatalogImage(file))) return;
      if (this.componentDestroyed || this.saving) return;
      const slot = this.nextComplementarySlot(product);
      if (!slot) {
        this.$toast.error("Cada produto pode ter no máximo três fotos.");
        return;
      }
      this.cropSelection = { product, slot, file };
    },
    async selectComplementaryImage(product, slot, event) {
      if (this.saving || this.imageValidationBusy) return;
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file || !(await this.validateCatalogImage(file))) return;
      if (this.componentDestroyed || this.saving) return;
      this.cropSelection = { product, slot, file };
    },
    applyCropSelection({ file, crop, preview }) {
      const selection = this.cropSelection;
      if (!selection || selection.file !== file) {
        URL.revokeObjectURL(preview);
        return;
      }
      if (selection.slot) {
        this.applyComplementaryImage(selection.product, selection.slot, file, crop, preview);
      } else {
        const draft = this.ensureDraft(selection.product);
        this.revokeDraftPreview(selection.product.id);
        draft.image_file = file;
        draft.image_crop = crop;
        draft.image_preview = preview;
        draft.remover_imagem = false;
      }
      this.cropSelection = null;
    },
    applyComplementaryImage(product, slot, file, crop, preview) {
      const draft = this.ensureDraft(product);
      const state = draft.complementary_images[String(slot)];
      this.revokeComplementaryPreview(product.id, slot);
      state.image_file = file;
      state.image_crop = crop;
      state.image_preview = preview;
      state.removed = false;
      this.syncComplementaryRemovalSlots(draft);
    },
    removeComplementaryImage(product, slot) {
      if (this.saving || this.imageValidationBusy) return;
      const draft = this.ensureDraft(product);
      const state = draft.complementary_images[String(slot)];
      this.revokeComplementaryPreview(product.id, slot);
      state.image_file = null;
      state.image_preview = "";
      state.image_crop = null;
      state.removed = Boolean(state.original_url);
      this.syncComplementaryRemovalSlots(draft);
      this.cleanDraftIfUnchanged(product);
    },
    undoComplementaryImage(product, slot) {
      if (this.saving || this.imageValidationBusy) return;
      const draft = this.draftFor(product);
      if (!draft) return;
      const state = draft.complementary_images[String(slot)];
      this.revokeComplementaryPreview(product.id, slot);
      state.image_file = null;
      state.image_preview = "";
      state.image_crop = null;
      state.removed = false;
      this.syncComplementaryRemovalSlots(draft);
      this.cleanDraftIfUnchanged(product);
    },
    syncComplementaryRemovalSlots(draft) {
      draft.remover_imagens_complementares = COMPLEMENTARY_SLOTS.filter(
        (slot) => {
          const state = draft.complementary_images[String(slot)];
          return Boolean(state && state.removed && !state.image_file);
        }
      );
    },
    removeProductImage(product) {
      if (this.saving || this.imageValidationBusy) return;
      const draft = this.ensureDraft(product);
      this.revokeDraftPreview(product.id);
      draft.image_file = null;
      draft.image_preview = "";
      draft.image_crop = null;
      draft.remover_imagem = shouldRemoveCatalogMainImage(draft.original_image);
      this.cleanDraftIfUnchanged(product);
    },
    undoProductImage(product) {
      if (this.saving || this.imageValidationBusy) return;
      const draft = this.draftFor(product);
      if (!draft) return;
      this.revokeDraftPreview(product.id);
      draft.image_file = null;
      draft.image_preview = "";
      draft.image_crop = null;
      draft.remover_imagem = false;
      this.cleanDraftIfUnchanged(product);
    },
    revokeDraftPreview(id) {
      const draft = this.drafts[String(id)];
      if (
        draft &&
        draft.image_preview &&
        draft.image_preview.indexOf("blob:") === 0
      ) {
        URL.revokeObjectURL(draft.image_preview);
      }
      if (draft) draft.image_preview = "";
    },
    revokeComplementaryPreview(id, slot) {
      const draft = this.drafts[String(id)];
      const state =
        draft &&
        draft.complementary_images &&
        draft.complementary_images[String(slot)];
      if (
        state &&
        state.image_preview &&
        state.image_preview.indexOf("blob:") === 0
      ) {
        URL.revokeObjectURL(state.image_preview);
      }
      if (state) state.image_preview = "";
    },
    revokeAllDraftPreviews(id) {
      this.revokeDraftPreview(id);
      COMPLEMENTARY_SLOTS.forEach((slot) =>
        this.revokeComplementaryPreview(id, slot)
      );
    },
    cleanDraftIfUnchanged(product) {
      const key = String(product.id);
      const draft = this.drafts[key];
      if (!draft) return;
      const flagChanged =
        Boolean(draft.catalogo_digital) !==
        Boolean(draft.original_catalogo_digital);
      const imageChanged = Boolean(draft.image_file || draft.remover_imagem);
      const complementaryImageChanged = COMPLEMENTARY_SLOTS.some((slot) => {
        const state = draft.complementary_images[String(slot)];
        return Boolean(state && (state.image_file || state.removed));
      });
      const priceChanged = this.draftPriceChanged(draft);
      if (
        !flagChanged &&
        !imageChanged &&
        !complementaryImageChanged &&
        !priceChanged &&
        !draft.preco_erro
      ) {
        this.$delete(this.drafts, key);
      }
    },
    async saveAndPublish() {
      if (this.saving) return;
      if (this.imageValidationBusy) {
        this.$toast.error("Aguarde a validação da imagem antes de publicar.");
        return;
      }
      const dirtyDrafts = Object.keys(this.drafts).map((id) => this.drafts[id]);
      if (dirtyDrafts.length > 100) {
        this.$toast.error(
          "Publique até 100 alterações por vez para manter a operação segura."
        );
        return;
      }

      const missingCrop = dirtyDrafts.some((draft) => {
        if (draft.image_file && !draft.image_crop) return true;
        return COMPLEMENTARY_SLOTS.some((slot) => {
          const image = draft.complementary_images[String(slot)];
          return Boolean(image && image.image_file && !image.image_crop);
        });
      });
      if (missingCrop) {
        this.$toast.error("Ajuste o enquadramento das fotos antes de publicar.");
        return;
      }

      const invalidPriceDraft = dirtyDrafts.find(
        (draft) =>
          this.draftPriceChanged(draft) && !this.validateProductPrice(draft)
      );
      if (invalidPriceDraft) {
        this.$toast.error("Revise os preços destacados antes de publicar.");
        return;
      }

      this.saving = true;
      const payload = new FormData();
      const items = dirtyDrafts.map(buildCatalogProductPatch);
      payload.append("itens", JSON.stringify(items));
      dirtyDrafts.forEach((draft) => {
        if (draft.image_file) {
          payload.append(`imagens[${draft.id}]`, draft.image_file);
          Object.keys(draft.image_crop).forEach((key) =>
            payload.append(`crops[${draft.id}][${key}]`, draft.image_crop[key])
          );
        }
        COMPLEMENTARY_SLOTS.forEach((slot) => {
          const state = draft.complementary_images[String(slot)];
          if (state && state.image_file) {
            payload.append(
              `imagens_complementares[${draft.id}][${slot}]`,
              state.image_file
            );
            Object.keys(state.image_crop).forEach((key) =>
              payload.append(`crops_complementares[${draft.id}][${slot}][${key}]`, state.image_crop[key])
            );
          }
        });
      });

      try {
        const data = await publishCatalogProducts(payload);
        Object.keys(this.drafts).forEach((id) =>
          this.revokeAllDraftPreviews(id)
        );
        this.drafts = {};
        this.$toast.success("Produtos salvos e catálogo publicado.");
        this.$emit("published", data || {});
        await this.loadProducts();
      } catch (error) {
        this.notifyError(error, "Não foi possível publicar os produtos.");
      } finally {
        this.saving = false;
        if (this.searchInput.trim() !== this.appliedSearch) {
          this.applySearch();
        }
      }
    },
    editProduct(product) {
      if (this.saving || this.imageValidationBusy) return;
      if (
        this.hasDrafts &&
        !window.confirm(
          "Existem alterações não publicadas. Deseja descartá-las e abrir o cadastro deste produto?"
        )
      ) {
        return;
      }
      this.allowRouteLeave = this.hasDrafts;
      this.$router.push({
        name: "produtosUpdate",
        params: { id_produto: product.id },
      });
    },
    consumeRouteLeaveApproval() {
      const approved = this.allowRouteLeave;
      this.allowRouteLeave = false;
      return approved;
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    notifyError(error, fallback) {
      const message =
        (error && error.response && error.response.data.message) ||
        (error && error.message) ||
        fallback;
      this.$toast.error(message || fallback);
    },
  },
};
</script>

<style scoped>
.catalog-products {
  --catalog-primary: var(--primary-color, #ee7361);
  padding-bottom: 104px;
}

.catalog-products__header,
.catalog-products__toolbar,
.catalog-products__pagination,
.catalog-products__publish-bar,
.catalog-products__publish-bar > div,
.catalog-product-photo,
.catalog-product-name small {
  display: flex;
  align-items: center;
}

.catalog-products__header {
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 22px;
}

.catalog-products__header h2 {
  margin: 5px 0 7px;
  color: #172033;
  font-size: 27px;
  letter-spacing: -0.03em;
}

.catalog-products__header p {
  max-width: 720px;
  margin: 0;
  color: #687084;
  line-height: 1.55;
}

.catalog-products__eyebrow {
  color: var(--catalog-primary);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
}

.catalog-products__draft-count {
  min-width: 150px;
  padding: 13px 17px;
  border: 1px solid color-mix(in srgb, var(--catalog-primary) 24%, #ffffff 76%);
  border-radius: 14px;
  color: #6b7280;
  background: color-mix(in srgb, var(--catalog-primary) 7%, #ffffff 93%);
  font-size: 12px;
  text-align: center;
}

.catalog-products__draft-count strong {
  display: block;
  color: var(--catalog-primary);
  font-size: 22px;
}

.catalog-products__toolbar {
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 15px;
}

.catalog-products__search {
  display: flex;
  align-items: center;
  width: min(100%, 520px);
  padding: 0 13px;
  border: 1px solid #e0e3eb;
  border-radius: 13px;
  background: #fff;
  box-shadow: 0 6px 20px rgba(23, 32, 51, 0.04);
}

.catalog-products__search > i {
  color: #8a91a1;
  font-size: 19px;
}

.catalog-products__search input {
  width: 100%;
  height: 46px;
  padding: 0 10px;
  border: 0;
  outline: none;
  color: #172033;
  background: transparent;
}

.catalog-products__search button,
.catalog-products__refresh,
.catalog-product-photo__actions button,
.catalog-product-edit,
.catalog-products__pagination button {
  border: 0;
  cursor: pointer;
}

.catalog-products__search button {
  color: #8a91a1;
  background: transparent;
  font-size: 18px;
}

.catalog-products__refresh {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 11px 15px;
  border: 1px solid #e0e3eb;
  border-radius: 11px;
  color: #4f5668;
  background: #fff;
  font-weight: 700;
}

.catalog-products__refresh:disabled,
.catalog-products__pagination button:disabled,
.catalog-products__publish-bar > button:disabled {
  cursor: not-allowed;
  opacity: 0.52;
}

.catalog-products__panel {
  overflow: hidden;
  border: 1px solid #e7e9f0;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 16px 45px rgba(31, 39, 65, 0.06);
}

.catalog-products__table-wrap {
  overflow-x: auto;
}

.catalog-products__table {
  width: 100%;
  min-width: 1040px;
  border-collapse: collapse;
}

.catalog-products__table th {
  padding: 14px 18px;
  border-bottom: 1px solid #eceef4;
  color: #7c8393;
  background: #fafbfc;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-align: left;
  text-transform: uppercase;
}

.catalog-products__table td {
  padding: 15px 18px;
  border-bottom: 1px solid #eff1f5;
  color: #172033;
  vertical-align: middle;
}

.catalog-products__table tbody tr:last-child td {
  border-bottom: 0;
}

.catalog-products__table tbody tr:hover {
  background: #fdfcff;
}

.catalog-products__table .is-photo {
  width: 355px;
}

.catalog-products__table .is-price {
  width: 125px;
  white-space: nowrap;
}

.catalog-products__table .is-publish {
  width: 145px;
}

.catalog-products__table .is-action {
  width: 96px;
  text-align: right;
}

.catalog-product-gallery {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  min-width: 315px;
}

.catalog-product-photo {
  flex: 0 0 78px;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}

.catalog-product-photo.is-principal {
  flex-basis: 130px;
}

.catalog-product-photo__label {
  min-height: 16px;
  color: #747b8b;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.07em;
  line-height: 16px;
  text-transform: uppercase;
}

.catalog-product-photo__preview {
  display: grid;
  flex: 0 0 66px;
  width: 66px;
  height: 66px;
  overflow: hidden;
  place-items: center;
  border: 1px solid #e6e8ef;
  border-radius: 13px;
  color: #a4a9b5;
  background: #f5f6f9;
  font-size: 24px;
}

.catalog-product-photo.is-principal .catalog-product-photo__preview {
  width: 72px;
  height: 72px;
  flex-basis: 72px;
  box-shadow: 0 5px 14px rgba(23, 32, 51, 0.08);
}

.catalog-product-photo__preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.catalog-product-photo__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 3px 7px;
  line-height: 1.35;
}

.catalog-product-photo__actions label,
.catalog-product-photo__actions button {
  padding: 0;
  color: var(--catalog-primary);
  background: transparent;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.catalog-product-photo__actions input.sr-only:focus + label,
.catalog-product-photo__actions input.sr-only:focus-visible + label {
  outline: 2px solid var(--catalog-primary);
  outline-offset: 3px;
  border-radius: 4px;
}

.catalog-product-photo__actions .is-add-complementary {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 2px;
  padding: 4px 7px;
  border: 1px solid color-mix(in srgb, var(--catalog-primary) 25%, #ffffff 75%);
  border-radius: 7px;
  background: color-mix(in srgb, var(--catalog-primary) 7%, #ffffff 93%);
}

.catalog-product-photo.is-complementary .catalog-product-photo__actions label,
.catalog-product-photo.is-complementary .catalog-product-photo__actions button {
  font-size: 10px;
}

.catalog-product-photo__actions label.is-disabled,
.catalog-product-photo__actions button:disabled,
.catalog-product-edit:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.catalog-product-photo__actions button {
  color: #d04c59;
}

.catalog-product-photo__actions .is-undo {
  color: #697083;
}

.catalog-product-name strong,
.catalog-product-name small,
.catalog-product-name em {
  display: block;
}

.catalog-product-price {
  position: relative;
  display: flex;
  align-items: center;
  width: 126px;
  margin: 0;
  border: 1px solid #dfe3eb;
  border-radius: 10px;
  background: #fff;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.catalog-product-price:focus-within {
  border-color: var(--catalog-primary);
  box-shadow: 0 0 0 3px
    color-mix(in srgb, var(--catalog-primary) 14%, transparent 86%);
}

.catalog-product-price > span {
  padding-left: 9px;
  color: #858b99;
  font-size: 11px;
  font-weight: 700;
}

.catalog-product-price input {
  min-width: 0;
  width: 100%;
  height: 38px;
  padding: 0 8px 0 5px;
  border: 0;
  outline: 0;
  color: #172033;
  background: transparent;
  font-size: 13px;
  font-weight: 750;
  text-align: right;
}

.catalog-product-price:has(input.is-invalid) {
  border-color: #d04c59;
}

.catalog-product-price small {
  position: absolute;
  top: calc(100% + 3px);
  right: 0;
  z-index: 2;
  width: 160px;
  padding: 5px 7px;
  border-radius: 6px;
  color: #fff;
  background: #d04c59;
  font-size: 9px;
  font-weight: 650;
  white-space: normal;
}

.catalog-product-name strong {
  margin-bottom: 4px;
  font-size: 14px;
}

.catalog-product-name small {
  flex-wrap: wrap;
  gap: 9px;
  color: #858b99;
}

.catalog-product-name small span + span::before {
  margin-right: 9px;
  content: "•";
}

.catalog-product-name em {
  max-width: 330px;
  margin-top: 6px;
  color: #b16922;
  font-size: 11px;
  font-style: normal;
  line-height: 1.4;
}

.catalog-product-toggle {
  display: inline-grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 6px 8px;
  margin: 0;
  cursor: pointer;
}

.catalog-product-toggle input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.catalog-product-toggle > span {
  position: relative;
  display: block;
  width: 39px;
  height: 22px;
  border-radius: 999px;
  background: #d8dbe3;
  transition: background 0.2s ease;
}

.catalog-product-toggle > span i {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 6px rgba(23, 32, 51, 0.22);
  transition: transform 0.2s ease;
}

.catalog-product-toggle input:checked + span {
  background: var(--catalog-primary);
}

.catalog-product-toggle input:checked + span i {
  transform: translateX(17px);
}

.catalog-product-toggle input:focus + span {
  box-shadow: 0 0 0 3px
    color-mix(in srgb, var(--catalog-primary) 18%, transparent 82%);
}

.catalog-product-toggle em {
  grid-column: 1 / -1;
  color: #747b8b;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
  text-transform: uppercase;
}

.catalog-product-toggle.is-disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.catalog-product-edit {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 10px;
  border-radius: 9px;
  color: var(--catalog-primary);
  background: color-mix(in srgb, var(--catalog-primary) 10%, #ffffff 90%);
  font-size: 12px;
  font-weight: 700;
}

.catalog-products__loading,
.catalog-products__empty {
  min-height: 310px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #737a8a;
}

.catalog-products__loading {
  gap: 10px;
}

.catalog-products__empty {
  flex-direction: column;
  padding: 38px;
  text-align: center;
}

.catalog-products__empty > span {
  display: grid;
  width: 56px;
  height: 56px;
  margin-bottom: 13px;
  place-items: center;
  border-radius: 17px;
  color: var(--catalog-primary);
  background: color-mix(in srgb, var(--catalog-primary) 10%, #ffffff 90%);
  font-size: 26px;
}

.catalog-products__empty h3 {
  margin: 0 0 5px;
  color: #172033;
}

.catalog-products__empty p {
  margin: 0;
}

.catalog-products__pagination {
  justify-content: space-between;
  gap: 20px;
  padding: 14px 18px;
  border-top: 1px solid #eceef4;
  background: #fafbfc;
}

.catalog-products__pagination p {
  margin: 0;
  color: #767d8d;
  font-size: 12px;
}

.catalog-products__pagination > div {
  display: flex;
  gap: 5px;
}

.catalog-products__pagination button {
  min-width: 33px;
  height: 33px;
  padding: 0 8px;
  border-radius: 9px;
  color: #61697a;
  background: #fff;
  box-shadow: inset 0 0 0 1px #e1e4eb;
  font-size: 12px;
  font-weight: 700;
}

.catalog-products__pagination button.active {
  color: #fff;
  background: var(--catalog-primary);
  box-shadow: none;
}

.catalog-products__publish-bar {
  position: sticky;
  z-index: 4;
  bottom: 18px;
  justify-content: space-between;
  gap: 22px;
  max-width: 980px;
  margin: 18px auto 0;
  padding: 14px 15px 14px 18px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 17px;
  color: #fff;
  background: color-mix(in srgb, var(--catalog-primary) 28%, #172033 72%);
  box-shadow: 0 20px 42px rgba(25, 31, 49, 0.24);
}

.catalog-products__publish-bar > div {
  gap: 12px;
}

.catalog-products__publish-bar > div > span {
  display: grid;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 11px;
  color: #fff;
  background: color-mix(in srgb, var(--catalog-primary) 62%, transparent 38%);
  font-size: 19px;
}

.catalog-products__publish-bar p,
.catalog-products__publish-bar strong,
.catalog-products__publish-bar small {
  display: block;
  margin: 0;
}

.catalog-products__publish-bar small {
  margin-top: 2px;
  color: #b7bdcb;
  font-size: 11px;
}

.catalog-products__publish-bar > button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 174px;
  padding: 12px 16px;
  border: 0;
  border-radius: 11px;
  color: #fff;
  background: var(--catalog-primary);
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.catalog-products__spinner {
  display: inline-block;
  width: 17px;
  height: 17px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: catalog-products-spin 0.75s linear infinite;
}

@keyframes catalog-products-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 780px) {
  .catalog-products__header,
  .catalog-products__toolbar,
  .catalog-products__pagination,
  .catalog-products__publish-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .catalog-products__draft-count {
    align-self: flex-start;
  }

  .catalog-products__search {
    width: 100%;
  }

  .catalog-products__publish-bar > button {
    width: 100%;
  }
}
</style>
